import React from 'react';
import type { UploadEntry } from './uploadTypes.js';
import { formatBytes, formatSpeed, formatEta } from './portalUtils.js';

interface StatsBarProps {
  entries: UploadEntry[];
}

export function StatsBar({ entries }: StatsBarProps): React.ReactElement | null {
  if (entries.length === 0) return null;

  const totalFiles = entries.length;
  const completeFiles = entries.filter((e) => e.status === 'complete').length;
  const failedFiles = entries.filter((e) => e.status === 'failed').length;
  const activeFiles = entries.filter((e) => e.status === 'uploading').length;

  const totalBytes = entries.reduce((sum, e) => sum + e.file.size, 0);
  const uploadedBytes = entries.reduce((sum, e) => sum + e.bytesUploaded, 0);
  const overallPct = totalBytes > 0 ? Math.round((uploadedBytes / totalBytes) * 100) : 0;

  // Aggregate speed is sum of all active upload speeds
  const totalSpeedBps = entries
    .filter((e) => e.status === 'uploading')
    .reduce((sum, e) => sum + e.speedBps, 0);

  // Overall ETA: remaining bytes / aggregate speed
  const remainingBytes = totalBytes - uploadedBytes;
  const overallEta = totalSpeedBps > 0 ? remainingBytes / totalSpeedBps : 0;

  const allDone = completeFiles + failedFiles === totalFiles;

  return (
    <div
      className="rounded-2xl p-4"
      style={{
        background: 'var(--glass-bg)',
        backdropFilter: 'blur(var(--glass-blur))',
        WebkitBackdropFilter: 'blur(var(--glass-blur))',
        border: '1px solid var(--glass-border)',
        boxShadow: '0 2px 12px rgba(0,0,0,0.25)',
      }}
      aria-label="Overall upload progress"
    >
      {/* Stats row */}
      <div className="flex items-center gap-4 flex-wrap mb-3">
        {/* Files counter */}
        <div className="flex items-center gap-1.5">
          <span className="text-xs uppercase tracking-widest font-semibold" style={{ color: 'var(--text-dimmed)' }}>Files</span>
          <span className="text-sm font-bold tabular-nums" style={{ color: 'var(--text-primary)' }}>
            {completeFiles}
            <span style={{ color: 'var(--text-dimmed)' }}> / {totalFiles}</span>
          </span>
          {failedFiles > 0 && (
            <span
              className="rounded-full px-1.5 py-0.5 text-[10px] font-bold"
              style={{ background: 'rgba(238,93,80,0.2)', color: 'var(--accent-red)' }}
            >
              {failedFiles} failed
            </span>
          )}
          {activeFiles > 0 && (
            <span
              className="rounded-full px-1.5 py-0.5 text-[10px] font-bold"
              style={{ background: 'rgba(13,207,184,0.15)', color: 'var(--accent-blue)' }}
            >
              {activeFiles} active
            </span>
          )}
        </div>

        <div className="text-xs" style={{ color: 'var(--border)' }}>·</div>

        {/* Data counter */}
        <div className="flex items-center gap-1.5">
          <span className="text-xs uppercase tracking-widest font-semibold" style={{ color: 'var(--text-dimmed)' }}>Data</span>
          <span className="text-sm font-bold tabular-nums" style={{ color: 'var(--text-primary)' }}>
            {formatBytes(uploadedBytes)}
            <span style={{ color: 'var(--text-dimmed)' }}> / {formatBytes(totalBytes)}</span>
          </span>
        </div>

        {totalSpeedBps > 0 && (
          <>
            <div className="text-xs" style={{ color: 'var(--border)' }}>·</div>
            <div className="flex items-center gap-1.5">
              <span className="text-xs uppercase tracking-widest font-semibold" style={{ color: 'var(--text-dimmed)' }}>Speed</span>
              <span
                className="text-sm font-bold tabular-nums"
                style={{ color: 'var(--accent-blue)' }}
              >
                {formatSpeed(totalSpeedBps)}
              </span>
            </div>
          </>
        )}

        {overallEta > 0 && (
          <>
            <div className="text-xs" style={{ color: 'var(--border)' }}>·</div>
            <div className="flex items-center gap-1.5">
              <span className="text-xs uppercase tracking-widest font-semibold" style={{ color: 'var(--text-dimmed)' }}>ETA</span>
              <span className="text-sm font-bold tabular-nums" style={{ color: 'var(--text-secondary)' }}>
                {formatEta(overallEta)}
              </span>
            </div>
          </>
        )}

        {/* Push pct to the right */}
        <div className="flex-1" />

        <span
          className="text-sm font-bold tabular-nums"
          style={{ color: allDone ? 'var(--accent-green)' : 'var(--text-primary)' }}
        >
          {overallPct}%
        </span>
      </div>

      {/* Overall progress bar */}
      <div
        className="h-2 rounded-full overflow-hidden"
        style={{ background: 'var(--bg-tertiary)' }}
        role="progressbar"
        aria-valuenow={overallPct}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label="Overall upload progress"
      >
        <div
          className="h-full rounded-full transition-all duration-500 ease-out"
          style={{
            width: `${overallPct}%`,
            background: allDone && failedFiles === 0
              ? 'var(--accent-green)'
              : 'linear-gradient(90deg, #0dcfb8 0%, #06b6d4 100%)',
          }}
        />
      </div>
    </div>
  );
}
